import { z } from 'zod';
import { TenantIdSchema, UlidSchema, IsoTimestampSchema } from './common.js';

/**
 * Audit Log — registro inmutable (append-only) de mutaciones del dominio.
 * Implementa hash chain SHA-256 para detectar manipulación retroactiva.
 * Parte del diferenciador de "transparencia hacia el residente".
 */

export const AuditEventTypeSchema = z.enum([
  'condominio.created',
  'condominio.updated',
  'unidad.created',
  'unidad.updated',
  'residente.created',
  'residente.updated',
  'residente.deactivated',
  'unidad_residente.assigned',
  'unidad_residente.released',
  'gasto_comun.emitido',
  'gasto_comun.anulado',
  'cargo_unidad.created',
  'cargo_unidad.paid',
  'pago.iniciado',
  'pago.reconciliado',
  'pago.rechazado',
  'asamblea.convocada',
  'asamblea.cerrada',
  'asamblea.anulada',
  'asamblea.votacion_abierta',
  'asamblea.votacion_cerrada',
  'asamblea.sin_quorum',
  'asamblea.votacion_extendida',
  'asamblea.segunda_citacion_declarada',
  'asamblea.materia_aprobada',
  'asamblea.materia_rechazada',
  'acta.firmada',
  'acta.generated',
  'visitor_pass.created',
  'visitor_pass.used',
  'feature_flags.updated',
  'fondo.ingreso_registrado',
  'fondo.egreso_registrado',
  'cargo.vencido',
  'fondo.auto_asignacion',
  'fondo.reversion',
  'membership.granted',
  'membership.revoked',
  'paquete.registrado',
  'paquete.retirado',
  'paquete.devuelto',
  'paquete.anulado',
]);
export type AuditEventType = z.infer<typeof AuditEventTypeSchema>;

export const AuditLogEntrySchema = z.object({
  id: UlidSchema,
  tenantId: TenantIdSchema,
  eventType: AuditEventTypeSchema,
  /** Tipo de entidad afectada (ej: "GastoComun"). */
  entityType: z.string(),
  /** ID de la entidad afectada. */
  entityId: z.string(),
  /** Usuario que disparó el cambio. */
  actor: z.object({
    userId: z.string(),
    rol: z.enum(['super_admin', 'admin', 'comite', 'conserje', 'propietario', 'residente', 'system']),
  }),
  /** Snapshot del estado relevante (no toda la entidad — solo lo cambiado). */
  payload: z.record(z.string(), z.unknown()),
  /** Hash SHA-256 del entry anterior del mismo tenant (hash chain). */
  hashPrev: z.string().regex(/^[a-f0-9]{64}$/).nullable(),
  /** Hash SHA-256 del contenido actual (entityType+entityId+payload+hashPrev). */
  hashCurrent: z.string().regex(/^[a-f0-9]{64}$/),
  timestamp: IsoTimestampSchema,
});
export type AuditLogEntry = z.infer<typeof AuditLogEntrySchema>;
